<script setup>
import NavigationBar from "@/components/Layout/NavigationBar.vue";
import { ref } from "vue";
import Swal from "sweetalert2";
// import { postContacto } from "@/utils/api_backend"; // Backend request commented out
import heroImage from "@/assets/img/Tactical/tactical_07.jpg";
import { loadSlim } from "tsparticles-slim";

// CONFIG PARTICLES
const particleConfig = {
  background: { color: "transparent" },
  particles: {
    number: { value: 40 },
    color: { value: "#ff3131" },
    shape: { type: "circle" },
    opacity: { value: 0.25 },
    size: { value: 3 },
    move: { enable: true, speed: 1.2, direction: "none", outModes: "out" },
  },
};

const particlesInit = async (engine) => {
  await loadSlim(engine);
};

// FORM DATA
const name = ref("");
const email = ref("");
const subject = ref("");
const message = ref("");
const loading = ref(false);

const sendForm = async () => {
  loading.value = true;
  try {
    // Simulación de envío
    await new Promise(resolve => setTimeout(resolve, 1500));

    await Swal.fire({
      title: "REQUEST RECEIVED",
      text: "Our instructors will review your message shortly.",
      icon: "success",
      background: '#111',
      color: '#fff',
      confirmButtonColor: '#ff3131',
      timer: 2500,
    });

    name.value = ""; email.value = ""; subject.value = ""; message.value = "";
  } catch (err) {
    console.error(err);
    await Swal.fire({
      title: "SYSTEM FAILURE",
      text: "Could not process contact request.",
      icon: "error",
      background: '#111',
      color: '#fff',
      confirmButtonColor: '#ff3131',
    });
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <section id="contact" class="contact-wrapper">
    <div class="hero-bg-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
    <div class="hero-gradient"></div>
    <Particles id="contactParticles" :init="particlesInit" :options="particleConfig" />
    <div class="smoke-layer"></div>

    <NavigationBar />

    <div class="container content-z">
      <div class="header-minimal text-center" data-aos="fade-down">
        <h3 class="display-6 fw-900 text-white">Do you have any questions? Write to us and we will gladly help you.</h3>
      </div>

      <div class="contact-grid mt-4" data-aos="fade-up">
        <div class="left-grid">
          <article class="info-card-tactical">
            <div class="tactical-icon">
              <i class="bi bi-geo-alt-fill"></i>
            </div>
            <h3 class="info-card__title">LOCATION</h3>
            <p class="info-card__value text-uppercase">Valdez Sánchez #274, Saltillo, Coahuila</p>
          </article>

          <div class="subgrid">
            <article class="info-card-tactical">
              <div class="tactical-icon">
                <i class="bi bi-telephone-fill"></i>
              </div>
              <h3 class="info-card__title">PHONE</h3>
              <p class="info-card__value">844 696 5591</p>
            </article>

            <article class="info-card-tactical">
              <div class="tactical-icon">
                <i class="bi bi-envelope-open-fill"></i>
              </div>
              <h3 class="info-card__title">EMAIL</h3>
              <p class="info-card__value">contact@evolve.com</p>
            </article>
          </div>
        </div>

        <div class="form-wrapper-tactical">
          <form @submit.prevent="sendForm">
            <div class="form-group">
              <input v-model="name" type="text" placeholder="FULL NAME" required />
            </div>
            <div class="form-group">
              <input v-model="email" type="email" placeholder="EMAIL ADDRESS" required />
            </div>
            <div class="form-group">
              <input v-model="subject" type="text" placeholder="MISSION SUBJECT" required />
            </div>
            <div class="form-group">
              <textarea v-model="message" placeholder="MESSAGE / REQUIREMENTS" rows="4" required></textarea>
            </div>

            <button type="submit" class="btn-tactical-submit" :disabled="loading">
              <span v-if="loading">PROCESSING...</span>
              <span v-else>SEND MESSAGE <i class="bi bi-send-fill ms-2"></i></span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.contact-wrapper {
  position: relative;
  min-height: 100vh;
  padding-bottom: 60px;
  overflow: hidden;
  background: #000;
}

.content-z {
  position: relative;
  z-index: 10;
  margin-top: 40px; /* Separación sutil del Navbar */
}

.header-minimal {
  padding-bottom: 20px;
}

.fw-900 { font-weight: 900; }

.minimal-subtitle {
  font-size: 1rem;
  color: #aaa;
  font-weight: 500;
  max-width: 600px;
  margin: 0 auto;
}

.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: brightness(0.2) contrast(1.1);
  z-index: 1;
}

.hero-gradient {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.85) 100%);
  z-index: 2;
}

/* GRID PRINCIPAL */
.contact-grid {
  width: 100%;
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr 1fr;
}

/* CARDS TÁCTICAS */
.info-card-tactical {
  background: rgba(10, 10, 10, 0.9);
  border: 1px solid #222;
  padding: 2rem 1.5rem;
  backdrop-filter: blur(10px);
  text-align: center;
}

.tactical-icon {
  width: 50px;
  height: 50px;
  background: #ff3131;
  display: grid;
  place-items: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
  color: white;
  clip-path: polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%);
}

.info-card__title {
  color: white;
  font-size: 1rem;
  font-weight: 900;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;
}

.info-card__value { color: #888; font-size: 0.9rem; }

/* SUBGRID */
.left-grid { display: grid; gap: 1.5rem; }
.subgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }

/* FORMULARIO */
.form-wrapper-tactical {
  background: rgba(10, 10, 10, 0.9);
  padding: 2rem;
  border: 1px solid #222;
  backdrop-filter: blur(10px);
}

.form-group input, 
.form-group textarea {
  width: 100%;
  padding: 12px;
  background: #000;
  border: 1px solid #333;
  color: #fff;
  border-radius: 0;
  margin-bottom: 1rem;
}

.btn-tactical-submit {
  width: 100%;
  padding: 14px;
  background: #ff3131;
  color: #fff;
  border: none;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
}

/* RESPONSIVE */
@media (max-width: 991px) {
  .contact-grid { grid-template-columns: 1fr; }
}

@media (max-width: 767px) {
  .subgrid { grid-template-columns: 1fr; }
  .display-4 { font-size: 2.2rem; }
}

.smoke-layer {
  position: absolute;
  inset: 0;
  background: url("https://c4.wallpaperflare.com/wallpaper/448/699/737/abstract-digital-art-3d-abstract-lines-wallpaper-preview.jpg");
  opacity: 0.08;
  mix-blend-mode: screen;
  z-index: 4;
  pointer-events: none;
}

/* CARDS TÁCTICAS - Ajuste con Transición */
.info-card-tactical {
  background: rgba(10, 10, 10, 0.9);
  border: 1px solid #222;
  padding: 2rem 1.5rem;
  backdrop-filter: blur(10px);
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); /* Transición suave */
}

/* EFECTO HOVER EN CARDS */
.info-card-tactical:hover {
  transform: translateY(-8px); /* Elevación */
  border-color: #ff3131; /* Cambio a rojo táctico */
  box-shadow: 0 10px 30px rgba(255, 49, 49, 0.2); /* Brillo sutil */
  background: rgba(20, 20, 20, 0.95);
}

/* FORM WRAPPER - Ajuste con Transición */
.form-wrapper-tactical {
  background: rgba(10, 10, 10, 0.9);
  padding: 2rem;
  border: 1px solid #222;
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* EFECTO HOVER EN FORMULARIO */
.form-wrapper-tactical:hover {
  border-color: rgba(255, 49, 49, 0.5);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

/* INPUTS - Efecto Focus (Hover de teclado) */
.form-group input, 
.form-group textarea {
  width: 100%;
  padding: 12px;
  background: #000;
  border: 1px solid #333;
  color: #fff;
  border-radius: 0;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
}

.form-group input:focus, 
.form-group textarea:focus {
  border-color: #ff3131;
  background: rgba(255, 49, 49, 0.05);
  outline: none;
  box-shadow: 0 0 10px rgba(255, 49, 49, 0.1);
}

/* BOTÓN - Hover */
.btn-tactical-submit {
  width: 100%;
  padding: 14px;
  background: #ff3131;
  color: #fff;
  border: none;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-tactical-submit:hover:not(:disabled) {
  background: #d62828;
  letter-spacing: 3px; /* Efecto de expansión de texto */
  box-shadow: 0 0 20px rgba(255, 49, 49, 0.4);
}
</style>